/* eslint-disable max-len, no-shadow, no-param-reassign, no-prototype-builtins */
import mutationTypes from '@root/src/javascripts/store/mutation-types'
import { log as logToSentry } from '@helper/sentry'
import moment from 'moment'
import { isString as _isString } from 'lodash'
import { isValidDate, repairState, resetState } from '../state-helper'

const rangeObj = {
  value: null,
  step: null,
  min: null,
  max: null,
}
const productDetailsContent = {
  liability: {
    initial: false,
    insurance_sum: {
      value: null,
    },
    deductible: { ...rangeObj },
    deductible_under_25: {
      value: null,
    },
  },
  partially_comprehensive: {
    initial: false,
    deductible: { ...rangeObj },
    glass: {
      value: '',
      collection: null,
    },
    personal_effects_coverage: { ...rangeObj },
    deductible_personal_effects_coverage: {
      value: null,
    },
    replacement_value_supplement: {
      value: false,
    },
    free_garage: {
      value: false,
    },
    stand_still_risk: {
      checked: false,
    },
    park_damage: {
      value: '',
      collection: null,
    },
    park_damage_deductible: { ...rangeObj },
    protective_clothing: { ...rangeObj },
  },
  fully_comprehensive: {
    initial: false,
    deductible: { ...rangeObj },
    deductible_under_25: {
      value: null,
    },
  },
  passenger_accident: {
    initial: false,
    bundle: {
      value: '',
      collection: null,
    },
    insured_persons: {
      value: null,
    },
    death_benefit: {
      value: null,
    },
    disability_capital: {
      value: null,
    },
    daily_compensation: {
      value: null,
    },
    hospital_compensation: {
      value: null,
    },
    medical_expenses: {
      value: false,
    },
  },
  gross_negligence: {
    initial: false,
  },
  '24_assistance': {
    initial: false,
    bundle: {
      value: '',
      collection: null,
    },
  },
  surcharges: {
    initial: false,
    daily_termination: {
      checked: false,
    },
    document_dispatch: {
      value: '',
      collection: null,
    },
    payment_frequency: {
      value: '',
      collection: null,
    },
    suspension: {
      checked: false,
    },
  },
}

// initial state
function initialState () {
  return {
    productDetails: {
      isSet: false,
      DexCarM: { ...productDetailsContent },
      DexCarL: { ...productDetailsContent },
      DexCarXl: { ...productDetailsContent },
    },
    id: null,
    declinedReason: null,
    customerType: 'person',
    searchmode: 'comfort',
    nationalitySwiss: null,
    nationalityOthers: null,
    comprehensive: '',
    productName: '',
    productHeadline: '',
    productKey: 'DexCarL',
    productPrice: '',
    productElementals: [],
    customized: false,
    nationalityName: '',
    youngDriver: '',
    tooManyAccidents: null,
    vehicleUsage: {
      business: null,
      transport: null,
      way_to_work: null,
      private: null,
    },
    contractStatus: null,
    productDocuments: {
      avbPdfUrl: null,
      pibPdfUrl: null,
    },
    insurance: {
      originalProposalId: null,
      productVersionId: null,
      productVersionKey: null,
      startOfInsurance: moment().format('YYYY-MM-DD'),
      paymentInterval: 'monthly',
      paymentType: 'credit_card',
      isKeeperMainDriver: null,
      isDriverTooYoung: null,
      genderOfTooYoungDriver: null,
      claims: null,
      driverLicenceWithdrawal: null,
      documentDispatch: {
        agentEmail: null,
        customerDocuments: [],
        emails: [],
        invoices: [],
        reminders: [],
      },
      avbPdfUrl: null,
      pibPdfUrl: null,
      vehicleRegistrationType: '01',
      billingModel: 'flex_drive',
    },
    tariffOptions: null,
    vehicle: {
      category: {
        label: '',
        id: null,
      },
      doors: null,
      type: '',
      power: {
        hp: null,
        range: null,
      },
      transmission: {
        id: null,
        label: null,
      },
      initialRegistrationYear: null,
      typeCertificateNumber: null,
      brand: null,
      model: null,
      fuel: {
        id: null,
        label: null,
      },
      id: null,
      canton: '',
      ownedSince: null,
      leasing: null,
      price: null,
      vehicleType: null,
      truckExtEquipmentPrice: null,
      truckIntEquipmentPrice: null,
    },
    registrationDetails: {
      serialNumber: null,
      registeredToDextra: null,
      licencePlateRegisteredToOwner: null,
      vehicleRegisteredToOwner: null,
      licencePlateActive: null,
      licencePlate: null,
      licencePlateAvailable: null,
    },
    personal: {
      gender: null,
      firstName: '',
      lastName: '',
      company: '',
      street: '',
      houseNumber: '',
      zip: '',
      city: '',
      nationality: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      age: null,
      driverLicenceSinceYear: null,
      residencePermit: null,
      industry: '',
      uid: '',
    },
    agent: {
      isAutoSense: false,
      isKasko2Go: false,
      malam: true,
    },
    applicationIdentifier: {
      id: null,
      token: null,
    },
    consultationProtocol: null,
    consultationProtocolFileName: null,
    keepExistingConsultationProtocol: true,
    whitelabelPartnerKey: null,
  }
}
export const state = initialState()

// mutations

function setPaymentType(state, newPaymentType) {
  const typeChanged = state.insurance.paymentType !== newPaymentType
  state.insurance.paymentType = newPaymentType
  if (newPaymentType === 'credit_card') {
    state.insurance.documentDispatch = { ...state.insurance.documentDispatch, invoices: ['customer'] }
  } else {
    if (!typeChanged) {
      return
    }
    state.insurance.documentDispatch = { ...state.insurance.documentDispatch, invoices: [] }
  }
}

export const mutations = {
  repairNewContractState (state) {
    repairState(state, initialState())
  },

  resetNewContractState (state) {
    resetState(state, initialState())
  },

  resetTariffOptions(state) {
    resetState(state.tariffOptions, {})
  },

  setProposalData(state, payload) {
    try {
      ['insurance', 'personal', 'vehicle', 'agent'].forEach((prop) => {
        Object.keys(payload[prop]).forEach((key) => {
          if (Object.keys(state[prop]).includes(key)) {
            state[prop][key] = payload[prop][key]
          }
        })
      })

      state.vehicle.initialRegistrationYear = payload.vehicle.firstRegistrationYear
      state.vehicle.model = payload.vehicle.modelShortDescription || payload.vehicle.model
      state.vehicle.vehicleType = payload.vehicle.vehicleType

      state.vehicle.modelLong = payload.vehicle.model
      state.tariffOptions = {}
      state.customized = true

      payload.tariffOptions.forEach((option) => {
        const newOption = { selected: option.selected }

        option.attributes.forEach((attribute) => {
          newOption[attribute.key] = attribute.value
        })

        state.tariffOptions[option.key] = newOption
      })

      state.productKey = payload.productVersion.product.key
      state.productVersion = payload.productVersion.key
      state.productName = payload.productVersion.product.name
      state.productHeadline = payload.productVersion.product.headline
      state.productDocuments.avbPdfUrl = payload.productVersion.product.avbPdfUrl
      state.productDocuments.pibPdfUrl = payload.productVersion.product.pibPdfUrl
      state.productElementals = payload.productVersion.elementals
      state.insurance.productVersionId = payload.productVersion.id
      state.insurance.productVersionKey = payload.productVersion.key
      state.insurance.vehicleRegistrationType = payload.insurance.vehicleRegistrationType
      state.vehicleUsage.business = payload.vehicle.vehicleUses.indexOf('commercial') >= 0
      state.vehicleUsage.transport = payload.vehicle.vehicleUses.indexOf('commercial_third_party_invoice') >= 0
      state.vehicleUsage.private = payload.vehicle.vehicleUses.indexOf('private') >= 0
      state.vehicleUsage.way_to_work = payload.vehicle.vehicleUses.indexOf('way_to_work') >= 0
      state.youngDriver = payload.insurance.isDriverTooYoung ? payload.insurance.genderOfTooYoungDriver : 'no'
      state.tooManyAccidents = state.insurance.claims > 2
      state.consultationProtocolFileName = payload.consultationProtocolFileName
      state.consultationProtocol = null
      state.whitelabelPartnerKey = payload.whitelabelPartnerKey

      state.agent.isAutoSense = payload.agent.internalId === 100110
      state.agent.isKasko2Go = payload.agent.internalId === 100109

      state.customerType = payload.personal.gender === 'company' ? 'company' : 'person'
    } catch (e) {
      logToSentry('Incomplete / defunc proposalData: ', e)
    }
  },

  [mutationTypes.productDetails] (state, payload) {
    state.productDetails = { ...state.productDetails, ...payload }
  },
  [mutationTypes.customerType] (state, payload) {
    state.customerType = payload
  },

  [mutationTypes.searchmode] (state, payload) {
    state.searchmode = payload
  },

  [mutationTypes.comprehensive] (state, payload) {
    state.comprehensive = payload
  },

  [mutationTypes.nationalitySwiss] (state, payload) {
    state.nationalitySwiss = payload
  },

  [mutationTypes.nationalityOthers] (state, payload) {
    state.nationalityOthers = payload
  },

  [mutationTypes.initialRegistrationYear] (state, payload) {
    state.vehicle = { ...state.vehicle, initialRegistrationYear: ~~payload }
  },

  [mutationTypes.typeCertificateNumber] (state, payload) {
    state.vehicle = { ...state.vehicle, typeCertificateNumber: payload }
  },

  [mutationTypes.brand] (state, payload) {
    state.vehicle = { ...state.vehicle, brand: payload }
  },

  [mutationTypes.model] (state, payload) {
    state.vehicle = { ...state.vehicle, model: payload, modelLong: null }
  },

  [mutationTypes.fuelTypeId] (state, payload) {
    state.vehicle.fuel = { ...state.vehicle.fuel, id: payload }
  },

  [mutationTypes.fuelTypeName] (state, payload) {
    state.vehicle.fuel = { ...state.vehicle.fuel, label: payload }
  },

  [mutationTypes.vehiclePowerRange] (state, payload) {
    state.vehicle.power = { ...state.vehicle.power, range: payload }
  },

  [mutationTypes.vehiclePower] (state, payload) {
    state.vehicle.power = { ...state.vehicle.power, hp: payload }
  },

  [mutationTypes.vehicleCategory] (state, payload) {
    state.vehicle.category = { ...state.vehicle.category, id: payload }
  },

  [mutationTypes.vehicleCategoryName] (state, payload) {
    state.vehicle.category = { ...state.vehicle.category, label: payload }
  },

  [mutationTypes.vehicleType] (state, payload) {
    state.vehicle = { ...state.vehicle, type: payload }
  },

  [mutationTypes.truckExtEquipmentPrice] (state, payload) {
    // eslint-disable-next-line no-restricted-globals
    const price = isNaN(payload || 0) ? 0 : Number(payload || 0)
    state.vehicle = { ...state.vehicle, truckExtEquipmentPrice: price }
  },

  [mutationTypes.truckIntEquipmentPrice] (state, payload) {
    // eslint-disable-next-line no-restricted-globals
    const price = isNaN(payload || 0) ? 0 : Number(payload || 0)
    state.vehicle = { ...state.vehicle, truckIntEquipmentPrice: price }
  },

  [mutationTypes.vehicleDoors] (state, payload) {
    state.vehicle = { ...state.vehicle, doors: payload }
  },

  [mutationTypes.transmission] (state, payload) {
    state.vehicle.transmission = { ...state.vehicle.transmission, id: payload }
  },

  [mutationTypes.transmissionName] (state, payload) {
    state.vehicle.transmission = { ...state.vehicle.transmission, label: payload }
  },

  [mutationTypes.vehicleId] (state, payload) {
    state.vehicle = { ...state.vehicle, id: payload }
  },

  [mutationTypes.vehicleCategoryType] (state, payload) {
    state.vehicle = { ...state.vehicle, vehicleType: payload }
    state.insurance = { ...state.insurance, vehicleRegistrationType: payload }
  },

  [mutationTypes.vehicleRegistrationType] (state, payload) {
    state.insurance = { ...state.insurance, vehicleRegistrationType: payload }
  },

  [mutationTypes.canton] (state, payload) {
    state.vehicle = { ...state.vehicle, canton: payload }
  },

  [mutationTypes.ownedSince] (state, payload) {
    state.vehicle = { ...state.vehicle, ownedSince: ~~payload }
  },

  [mutationTypes.businessUsage] (state, payload) {
    state.vehicleUsage.business = payload
  },

  [mutationTypes.transport] (state, payload) {
    state.vehicleUsage.transport = payload
  },

  [mutationTypes.leasing] (state, payload) {
    state.vehicle = { ...state.vehicle, leasing: payload }
  },

  [mutationTypes.vehiclePrice] (state, payload) {
    state.vehicle = { ...state.vehicle, price: payload }
  },

  [mutationTypes.nationality] (state, payload) {
    state.personal = { ...state.personal, nationality: payload }
  },

  [mutationTypes.nationalityName] (state, payload) {
    state.nationalityName = payload
  },

  [mutationTypes.residencePermit] (state, payload) {
    state.personal = { ...state.personal, residencePermit: payload }
  },

  [mutationTypes.driverLicenceSinceYear] (state, payload) {
    state.personal = { ...state.personal, driverLicenceSinceYear: ~~payload }
  },

  [mutationTypes.isKeeperMainDriver] (state, payload) {
    state.insurance = { ...state.insurance, isKeeperMainDriver: payload }
  },

  [mutationTypes.isDriverTooYoung] (state, payload) {
    state.insurance = { ...state.insurance, isDriverTooYoung: payload }
  },

  [mutationTypes.genderOfTooYoungDriver] (state, payload) {
    state.insurance = { ...state.insurance, genderOfTooYoungDriver: payload }
  },

  [mutationTypes.claims] (state, payload) {
    state.insurance = { ...state.insurance, claims: payload }
  },

  [mutationTypes.customerDocuments] (state, payload) {
    const formattedPayload = _isString(payload) ? payload.split(',') : payload
    state.insurance.documentDispatch = { ...state.insurance.documentDispatch, customerDocuments: formattedPayload }
  },
  [mutationTypes.invoices] (state, payload) {
    const formattedPayload = _isString(payload) ? payload.split(',') : payload
    state.insurance.documentDispatch = { ...state.insurance.documentDispatch, invoices: formattedPayload }
  },

  [mutationTypes.reminders] (state, payload) {
    const formattedPayload = _isString(payload) ? payload.split(',') : payload
    state.insurance.documentDispatch = { ...state.insurance.documentDispatch, reminders: formattedPayload }
  },

  [mutationTypes.agentEmail] (state, payload) {
    state.insurance.documentDispatch = { ...state.insurance.documentDispatch, agentEmail: payload }
  },

  [mutationTypes.emails] (state, payload) {
    const formattedPayload = _isString(payload) ? payload.split(',') : payload
    state.insurance.documentDispatch = { ...state.insurance.documentDispatch, emails: formattedPayload }
  },

  [mutationTypes.consultationProtocol] (state, payload) {
    if (!(payload instanceof File)) {
      payload = null
    }
    state.consultationProtocol = payload
  },

  [mutationTypes.consultationProtocolFileName] (state, payload) {
    state.consultationProtocolFileName = payload
  },

  [mutationTypes.whitelabelPartnerKey] (state, payload) {
    state.whitelabelPartnerKey = payload
  },

  [mutationTypes.keepExistingConsultationProtocol] (state, payload) {
    state.keepExistingConsultationProtocol = payload
  },

  [mutationTypes.youngDriver] (state, payload) {
    state.youngDriver = payload
  },

  [mutationTypes.driverLicenceWithdrawal] (state, payload) {
    state.insurance = { ...state.insurance, driverLicenceWithdrawal: payload }
  },

  [mutationTypes.tooManyAccidents] (state, payload) {
    state.tooManyAccidents = payload
  },

  [mutationTypes.gender] (state, payload) {
    state.personal = { ...state.personal, gender: payload }
  },

  [mutationTypes.firstName] (state, payload) {
    state.personal = { ...state.personal, firstName: payload }
  },

  [mutationTypes.lastName] (state, payload) {
    state.personal = { ...state.personal, lastName: payload }
  },

  [mutationTypes.street] (state, payload) {
    state.personal = { ...state.personal, street: payload }
  },

  [mutationTypes.houseNumber] (state, payload) {
    state.personal = { ...state.personal, houseNumber: payload }
  },

  [mutationTypes.zip] (state, payload) {
    state.personal = { ...state.personal, zip: payload }
  },

  [mutationTypes.city] (state, payload) {
    state.personal = { ...state.personal, city: payload }
  },

  [mutationTypes.email] (state, payload) {
    state.personal = { ...state.personal, email: payload }
  },

  [mutationTypes.phone] (state, payload) {
    state.personal = { ...state.personal, phone: payload }
  },

  [mutationTypes.age] (state, payload) {
    state.personal = { ...state.personal, age: ~~payload }
  },

  [mutationTypes.company] (state, payload) {
    state.personal = { ...state.personal, company: payload }
  },

  [mutationTypes.uid] (state, payload) {
    state.personal = { ...state.personal, uid: payload }
  },

  [mutationTypes.industry] (state, payload) {
    state.personal = { ...state.personal, industry: payload }
  },

  [mutationTypes.dateOfBirth] (state, payload) {
    if (isValidDate(payload)) {
      const [day, month, year] = payload.split('.')
      const ageDiff = Date.now() - new Date(year, month - 1, day).getTime()
      const ageDate = new Date(ageDiff)
      state.personal = { ...state.personal, dateOfBirth: moment(payload, 'DD.MM.YYYY').format('YYYY-MM-DD'), age: Math.abs(ageDate.getUTCFullYear() - 1970) }
    } else {
      state.personal = { ...state.personal, dateOfBirth: '' }
    }
  },

  [mutationTypes.startOfInsurance] (state, payload) {
    if (isValidDate(payload)) {
      state.insurance = { ...state.insurance, startOfInsurance: moment(payload, 'DD.MM.YYYY').format('YYYY-MM-DD') }
    } else {
      state.insurance = { ...state.insurance, startOfInsurance: '' }
    }
  },

  [mutationTypes.product] (state, payload) {
    state.insurance = { ...state.insurance, product: payload }
  },

  [mutationTypes.productName] (state, payload) {
    state.productName = payload
  },

  [mutationTypes.productHeadline] (state, payload) {
    state.productHeadline = payload
  },

  [mutationTypes.productKey] (state, payload) {
    state.productKey = payload
  },

  [mutationTypes.productPrice] (state, payload) {
    state.productPrice = payload
  },

  [mutationTypes.productElementals] (state, payload) {
    state.productElementals = payload
  },

  [mutationTypes.productVersionId] (state, payload) {
    state.insurance = { ...state.insurance, productVersionId: payload }
  },

  [mutationTypes.productVersionKey] (state, payload) {
    state.insurance = { ...state.insurance, productVersionKey: payload }
  },

  [mutationTypes.productDocuments] (state, payload) {
    state.productDocuments = payload
  },

  [mutationTypes.customized] (state, payload) {
    state.customized = payload
  },

  [mutationTypes.declinedReason] (state, payload) {
    state.declinedReason = payload
  },

  [mutationTypes.newContractId] (state, payload) {
    state.id = payload
  },

  [mutationTypes.originalProposalId] (state, payload) {
    state.insurance = { ...state.insurance, originalProposalId: payload }
  },

  [mutationTypes.contractStatus] (state, payload) {
    state.contractStatus = payload
  },

  [mutationTypes.paymentInterval] (state, payload) {
    state.insurance = { ...state.insurance, paymentInterval: payload }
  },

  [mutationTypes.paymentType] (state, payload) {
    state.insurance = { ...state.insurance, paymentType: payload }
  },

  [mutationTypes.tariffOptions] (state, payload) {
    // due to the nesting elemental/attribute structure we have to prepare the new state
    // in order to make sure that we do not override other attributes for this elemental
    const newState = { ...state.tariffOptions }
    if (!newState.hasOwnProperty(payload.elemental)) {
      newState[payload.elemental] = {}
    }
    newState[payload.elemental][payload.attribute] = payload.value

    state.tariffOptions = { ...state.tariffOptions, ...newState }
  },

  [mutationTypes.licencePlateAvailable] (state, payload) {
    state.registrationDetails = { ...state.registrationDetails, licencePlateAvailable: payload }
  },

  [mutationTypes.licencePlate] (state, payload) {
    state.registrationDetails = { ...state.registrationDetails, licencePlate: payload }
  },

  [mutationTypes.licencePlateActive] (state, payload) {
    state.registrationDetails = { ...state.registrationDetails, licencePlateActive: payload }
  },

  [mutationTypes.vehicleRegisteredToOwner] (state, payload) {
    state.registrationDetails = { ...state.registrationDetails, vehicleRegisteredToOwner: payload }
  },

  [mutationTypes.registeredToDextra] (state, payload) {
    state.registrationDetails = { ...state.registrationDetails, registeredToDextra: payload }
  },

  [mutationTypes.serialNumber] (state, payload) {
    state.registrationDetails = { ...state.registrationDetails, serialNumber: payload }
  },

  [mutationTypes.licencePlateRegisteredToOwner] (state, payload) {
    state.registrationDetails = { ...state.registrationDetails, licencePlateRegisteredToOwner: payload }
  },

  [mutationTypes.applicationIdentifier] (state, payload) {
    state.applicationIdentifier = {
      ...state.applicationIdentifier,
      id: payload.id,
      token: payload.token,
    }
  },
  [mutationTypes.billingModel] (state, payload) {
    let { paymentInterval } = state.insurance
    if (payload === 'flex_drive') {
      paymentInterval = 'monthly'
      setPaymentType(state, 'credit_card')
    } else {
      if (paymentInterval === 'monthly') {
        paymentInterval = 'yearly'
      }
      setPaymentType(state, 'ESR')
    }

    state.insurance = {
      ...state.insurance, billingModel: payload, paymentInterval,
    }
  },
}

export default {
  state,
  mutations,
}
